import { useEffect, useRef, useState } from 'react';
import { bindParticlePointer } from '../lib/particlePointer';
import { isometricCubePaths } from '../lib/canvasGeometry';

const DURATION = 2500;
const ISO_X = .8660254;
const ISO_Y = .5;
const clamp = value => Math.max(0, Math.min(1, value));
const noise = n => { const value = Math.sin(n * 127.1 + 91.3) * 43758.5453; return value - Math.floor(value); };

function makeApartment() {
  const cells = new Map();
  const add = (x, z, y, part, material) => cells.set(`${x}:${z}:${y}`, {
    x, z, y, part, material, seed: noise(x * 17 + z * 31 + y * 13),
    px: (x - z) * ISO_X, py: (x + z) * ISO_Y - y, dx: 0, dy: 0,
  });
  const block = (x1, x2, z1, z2, y1, y2, part, material) => {
    for (let x = x1; x <= x2; x++) for (let z = z1; z <= z2; z++) for (let y = y1; y <= y2; y++) add(x, z, y, part, material);
  };
  // One apartment, not several buildings: three rooms on one floor plate.
  for (let x = -13; x <= 13; x++) for (let z = -10; z <= 10; z++) add(x, z, -1, 0, x > 0 && z < -1 ? 'kitchen-floor' : 'floor');
  // Tall rear walls have real window openings. The roof and front walls are cut away.
  for (let x = -13; x <= 13; x++) for (let y = 0; y <= 5; y++) {
    const window = (x >= -9 && x <= -5 || x >= 5 && x <= 9) && y >= 2 && y <= 4;
    if (!window) add(x, -10, y, 1, 'wall');
  }
  for (let z = -9; z <= 10; z++) for (let y = 0; y <= 5; y++) {
    if (!(z >= -3 && z <= 1 && y >= 2 && y <= 4)) add(-13, z, y, 1, 'wall');
  }
  // Low cutaway partitions keep the furniture readable; gaps are doorways.
  for (let z = -9; z <= 9; z++) if (z < 1 || z > 3) block(0, 0, z, z, 0, 2, 1, 'wall');
  for (let x = 1; x <= 12; x++) if (x < 3 || x > 5) block(x, x, -1, -1, 0, 2, 1, 'wall');
  for (let z = -9; z <= 10; z++) add(13, z, 0, 1, 'wall');
  for (let x = -12; x <= 12; x++) if (x < 7 || x > 9) add(x, 10, 0, 1, 'wall');
  // Bedroom: bed frame, pale mattress, two pillows and headboard.
  block(-10, -5, 0, 6, 0, 0, 2, 'furniture');
  block(-10, -5, 0, 6, 1, 2, 2, 'linen');
  block(-10, -5, -1, -1, 0, 3, 2, 'furniture');
  block(-10, -8, 0, 1, 3, 3, 2, 'linen');
  block(-6, -5, 0, 1, 3, 3, 2, 'linen');
  // Living room: sofa with back/arms, coffee table with four legs.
  block(4, 10, 5, 7, 0, 1, 2, 'furniture');
  block(4, 10, 5, 5, 2, 3, 2, 'furniture');
  block(4, 4, 6, 7, 2, 3, 2, 'furniture');
  block(10, 10, 6, 7, 2, 3, 2, 'furniture');
  block(5, 9, 6, 7, 2, 2, 2, 'linen');
  block(5, 8, 1, 3, 2, 2, 2, 'furniture');
  for (const x of [5, 8]) for (const z of [1, 3]) block(x, x, z, z, 0, 1, 2, 'furniture');
  // Kitchen: L-shaped counter, worktop, dark hob and fridge.
  block(3, 11, -8, -7, 0, 1, 2, 'furniture');
  block(10, 11, -6, -4, 0, 1, 2, 'furniture');
  block(3, 11, -8, -7, 2, 2, 2, 'linen');
  block(10, 11, -6, -4, 2, 2, 2, 'linen');
  block(4, 5, -8, -7, 2, 2, 2, 'wall');
  block(1, 2, -9, -8, 0, 4, 2, 'linen');
  return [...cells.values()].sort((a, b) => a.x + a.z - b.x - b.z || a.y - b.y);
}
const APARTMENT = makeApartment();
const bounds = {
  left: Math.min(...APARTMENT.map(c => c.px)) - 2, right: Math.max(...APARTMENT.map(c => c.px)) + 2,
  top: Math.min(...APARTMENT.map(c => c.py)) - 2, bottom: Math.max(...APARTMENT.map(c => c.py)) + 2,
};
const centre = { x: (bounds.left + bounds.right) / 2, y: (bounds.top + bounds.bottom) / 2 };

export const RentAIApartmentCanvas = ({ reduced = false, active = false, label }) => {
  const ref = useRef(null), motion = useRef(reduced), replayRef = useRef(null), wakeRef = useRef(null);
  const [supported, setSupported] = useState(true);
  motion.current = reduced;
  useEffect(() => {
    const canvas = ref.current, stage = canvas.parentElement;
    let context;
    try { context = canvas.getContext('2d'); } catch { /* Static accessible fallback below. */ }
    if (!context) { setSupported(false); return; }
    const cells = APARTMENT.map(cell => ({ ...cell }));
    const style = getComputedStyle(stage), token = name => style.getPropertyValue(name).trim();
    const palette = { ink: token('--product-ink'), accent: token('--product-accent'), paper: token('--product-bg'), top: token('--product-cube-top'), left: token('--product-cube-left'), right: token('--product-cube-right') };
    const floorOpacity = Number(token('--rent-floor-opacity')) || .24, roomOpacity = Number(token('--rent-room-opacity')) || .34, cubeGap = Number(token('--rent-cube-gap')) || .92;
    const pointer = bindParticlePointer(canvas, { project: event => { const box = canvas.getBoundingClientRect(); return { x: event.clientX - box.left, y: event.clientY - box.top }; }, isReduced: () => motion.current });
    let width = 0, height = 0, unit = 1, dpr = 1, elapsed = 0, last = 0;
    let visible = false, started = false, disposed = false, raf = 0, resizeFrame = 0, replayCount = 0;
    let cubePaths;
    const polygon = (path, fill) => { context.fillStyle = fill; context.fill(path); };
    const drawCube = (x, y, cell, alpha) => {
      context.setTransform(dpr, 0, 0, dpr, x * dpr, y * dpr);
      const { left, right, top } = cubePaths;
      const floor = cell.part === 0, linen = cell.material === 'linen', furniture = cell.material === 'furniture';
      context.globalAlpha = alpha * (floor ? cell.material === 'kitchen-floor' ? roomOpacity : floorOpacity : 1);
      polygon(left, linen ? palette.paper : furniture || floor ? palette.accent : palette.ink); polygon(left, palette.left);
      polygon(right, linen ? palette.accent : palette.ink); polygon(right, palette.right);
      polygon(top, linen ? palette.paper : palette.accent); if (!linen) polygon(top, palette.top);
    };
    function draw(now, delta = 0) {
      if (!width || !height) return false;
      const reduce = motion.current;
      if (reduce) elapsed = DURATION;
      else if (started) elapsed = Math.min(DURATION, elapsed + delta);
      const progress = reduce ? 1 : elapsed / DURATION, contact = pointer.read(now), interactive = !reduce && contact.active;
      const radius = Math.min(width * .28, 105), blend = reduce ? 1 : 1 - Math.exp(-Math.max(delta, 16) * .012);
      let displacement = 0;
      context.setTransform(dpr, 0, 0, dpr, 0, 0); context.clearRect(0, 0, width, height);
      for (const cell of cells) {
        const delay = cell.part === 0 ? cell.seed * .08 : cell.part === 1 ? .20 + cell.y * .035 + cell.seed * .06 : .66 + cell.seed * .08;
        const local = reduce ? 1 : clamp((progress - delay) / (cell.part === 0 ? .21 : cell.part === 1 ? .24 : .25));
        if (!local) continue;
        const ease = 1 - (1 - local) ** 3;
        const bx = width / 2 + (cell.px - centre.x) * unit, by = height / 2 + (cell.py - centre.y) * unit;
        const vx = bx - contact.x, vy = by - contact.y, distance = interactive ? Math.hypot(vx, vy) || 1 : 1;
        const force = interactive ? Math.max(0, 1 - distance / radius) ** 2 * unit * 1.8 : 0;
        cell.dx += ((reduce ? 0 : vx / distance * force) - cell.dx) * blend;
        cell.dy += ((reduce ? 0 : vy / distance * force) - cell.dy) * blend;
        displacement = Math.max(displacement, Math.hypot(cell.dx, cell.dy));
        const flight = (1 - ease) * unit;
        drawCube(bx + (cell.seed - .5) * flight * 8 + cell.dx, by - flight * (6 + cell.seed * 9) + cell.dy, cell, local);
      }
      context.globalAlpha = 1;
      canvas.dataset.ready = 'true'; canvas.dataset.state = reduce ? 'static' : !started ? 'waiting' : progress < 1 ? 'assembling' : 'assembled';
      canvas.dataset.build = progress.toFixed(3); canvas.dataset.displacement = displacement.toFixed(2); canvas.dataset.particleCount = String(cells.length);
      canvas.dataset.roomCount = '3'; canvas.dataset.furnitureCount = '4'; canvas.dataset.replayCount = String(replayCount);
      canvas.dataset.pointerActive = String(interactive); canvas.dataset.input = contact.input; canvas.dataset.contact = String(contact.contact); canvas.dataset.lastEnd = contact.lastEnd;
      return !reduce && (started && progress < 1 || interactive || displacement > .05);
    }
    function frame(now) {
      raf = 0;
      if (disposed || !visible || document.hidden) return;
      if (last && now - last < 32) { raf = requestAnimationFrame(frame); return; }
      const delta = Math.min(70, last ? now - last : 16); last = now;
      if (draw(now, delta)) raf = requestAnimationFrame(frame);
    }
    function wake() { if (!disposed && visible && !document.hidden && !raf) { last = 0; raf = requestAnimationFrame(frame); } }
    const resize = () => {
      if (disposed) return;
      width = stage.clientWidth; height = stage.clientHeight; dpr = Math.min(devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
      unit = Math.min(width / (bounds.right - bounds.left + 5), height / (bounds.bottom - bounds.top + 5));
      cubePaths = isometricCubePaths(unit * cubeGap, ISO_X, ISO_Y);
      draw(performance.now()); wake();
    };
    const resizeObserver = new ResizeObserver(() => { cancelAnimationFrame(resizeFrame); resizeFrame = requestAnimationFrame(resize); });
    resizeObserver.observe(stage); resize();
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0]; visible = entry.isIntersecting;
      if (entry.intersectionRatio >= .4 && !started) { started = true; elapsed = 0; }
      if (visible) wake();
      else { cancelAnimationFrame(raf); raf = 0; last = 0; started = false; elapsed = 0; pointer.reset('offscreen'); }
    }, { threshold: [0, .4] });
    observer.observe(stage);
    const visibilityChange = () => { if (document.hidden) { cancelAnimationFrame(raf); raf = 0; } else wake(); };
    document.addEventListener('visibilitychange', visibilityChange);
    const events = ['pointermove', 'pointerdown', 'pointerup', 'pointerleave', 'pointercancel'];
    events.forEach(event => canvas.addEventListener(event, wake, { passive: true }));
    replayRef.current = () => {
      if (motion.current) return;
      elapsed = 0; started = true; replayCount++; pointer.reset('replay');
      cells.forEach(cell => { cell.dx = 0; cell.dy = 0; }); draw(performance.now()); wake();
    };
    wakeRef.current = () => { draw(performance.now()); wake(); };
    return () => {
      disposed = true; cancelAnimationFrame(raf); cancelAnimationFrame(resizeFrame); observer.disconnect(); resizeObserver.disconnect(); pointer.dispose();
      events.forEach(event => canvas.removeEventListener(event, wake)); document.removeEventListener('visibilitychange', visibilityChange);
      replayRef.current = null; wakeRef.current = null;
    };
  }, []);
  useEffect(() => { wakeRef.current?.(); }, [reduced]);
  useEffect(() => { if (active && !reduced) replayRef.current?.(); }, [active, reduced]);
  return <span className="court-ai-scales rent-ai-apartment" data-testid="rent-ai-apartment-figure"><span className="court-ai-scales-stage">
    <canvas ref={ref} hidden={!supported} role="img" aria-label={label} data-testid="rent-ai-tile-canvas" />
    {!supported && <svg className="court-ai-scales-fallback" viewBox={`${bounds.left - 2} ${bounds.top - 2} ${bounds.right - bounds.left + 4} ${bounds.bottom - bounds.top + 4}`} role="img" aria-label={label} data-testid="rent-ai-apartment-fallback">{APARTMENT.map((cell, i) => <g key={i} transform={`translate(${cell.px} ${cell.py})`} opacity={cell.part === 0 ? .25 : 1}><path d="M-.8,-.46 L0,0 L0,.92 L-.8,.46Z" fill={cell.material === 'linen' ? 'var(--product-bg)' : 'var(--product-ink)'} /><path d="M0,0 L.8,-.46 L.8,.46 L0,.92Z" fill="var(--product-ink)" /><path d="M0,-.92 L.8,-.46 L0,0 L-.8,-.46Z" fill={cell.material === 'linen' ? 'var(--product-bg)' : 'var(--product-accent)'} /></g>)}</svg>}
  </span></span>;
};
